import {isLoggedIn} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {dismissUserNoticePath} from '@github-ui/paths'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {useCallback, useMemo} from 'react'
import type {FilesPageNotices, UserNotice} from '../page-data/payloads/files'

/**
 * Hook to get filtered user notices based on feature flags and notice state.
 *
 * This hook:
 * 1. Filters out the `files_changed_opt_out` notice if the feature flag is disabled
 * 2. Filters out `compact_diff_lines` notice if `files_changed_opt_out` is active (not dismissed)
 *    to ensure we don't show both notices at the same time
 *
 * @param userNotices - Array of user notices from the payload.
 * @returns Filtered array of user notices
 */
export function useFilteredUserNotices(userNotices?: Array<UserNotice<FilesPageNotices>>) {
  const isFilesOptOutNoticeEnabled = isFeatureEnabled('pull_request_files_changed_opt_out_notice')

  return useMemo(() => {
    let notices = userNotices ?? []

    // Only show files_changed_opt_out notice if the feature flag is enabled
    if (!isFilesOptOutNoticeEnabled) {
      notices = notices.filter(notice => notice.name !== 'files_changed_opt_out')
    }

    // Filter out compact_diff_lines if files_changed_opt_out is active (not dismissed)
    // This ensures we don't show both notices at the same time
    const hasActiveFilesChangedOptOut = notices.some(
      notice => notice.name === 'files_changed_opt_out' && !notice.dismissed,
    )
    if (hasActiveFilesChangedOptOut) {
      return notices.filter(notice => notice.name !== 'compact_diff_lines')
    }
    return notices
  }, [userNotices, isFilesOptOutNoticeEnabled])
}

/**
 * Hook to get active (non-dismissed) user notices.
 *
 * @param userNotices - Array of user notices.
 * @returns Array of active user notices
 */
export function useUserNotices(userNotices?: Array<UserNotice<FilesPageNotices>>) {
  const notices = userNotices ?? []

  return notices.filter(notice => !notice.dismissed)
}

/**
 * Hook to check if a specific notice has been dismissed.
 *
 * @param notice - The notice name to check
 * @param userNotices - Array of user notices.
 * @returns true if the notice is dismissed or not found, false otherwise
 */
export function useIsNoticeDismissed(notice: FilesPageNotices, userNotices?: Array<UserNotice<FilesPageNotices>>) {
  const activeNotices = useUserNotices(userNotices)

  return activeNotices.filter(activeNotice => activeNotice.name === notice).length === 0
}

/**
 * Hook to dismiss a user notice.
 *
 * @param notice - The notice name to dismiss
 * @param userNotices - Array of user notices.
 * @returns Object containing dismissNotice function
 */
export function useDismissNotice(notice: FilesPageNotices, userNotices?: Array<UserNotice<FilesPageNotices>>) {
  const isNoticeDismissed = useIsNoticeDismissed(notice, userNotices)

  const dismissNotice = useCallback(() => {
    // Don't dismiss the notice if the user is not logged in or the notice is already dismissed
    if (!isLoggedIn() || isNoticeDismissed) return

    verifiedFetch(dismissUserNoticePath({noticeName: notice}), {method: 'POST'})
  }, [isNoticeDismissed, notice])

  return {dismissNotice}
}
